import React, { FC, useEffect, useState } from "react";
import { Modal, Form, Input, Button, message, Select } from "antd";
import { AnalyticsValidationRecord, CreateValidationRequest, UpdateValidationRequest, analyticsValidationService } from "../../services/analyticsValidationService";

interface ValidationModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    validation?: AnalyticsValidationRecord | null;
    mode: 'create' | 'edit';
}

const ValidationModal: FC<ValidationModalProps> = ({
    visible,
    onCancel,
    onSuccess,
    validation,
    mode
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [allValidations, setAllValidations] = useState<AnalyticsValidationRecord[]>([]);

    // Fetch all validations when modal is opened
    useEffect(() => {
        if (visible) {
            analyticsValidationService.getAllValidations()
                .then((data) => setAllValidations(data))
                .catch((err) => {
                    // Optionally handle error
                    setAllValidations([]);
                });
        }
    }, [visible]);

    useEffect(() => {
        if (visible && validation && mode === 'edit') {
            form.setFieldsValue({
                key: validation.key,
                value: validation.value
            });
        } else if (visible && mode === 'create') {
            form.resetFields();
        }
    }, [visible, validation, mode, form]);

    const isDuplicate = (key: string, value: string) => {
        // Case-insensitive check for key/value combo
        const keyLower = key.trim().toLowerCase();
        const valueLower = value.trim().toLowerCase();
        return allValidations.some((v) => {
            // If editing, skip the current record
            if (mode === 'edit' && validation && v.id === validation.id) return false;
            return v.key.trim().toLowerCase() === keyLower && v.value.trim().toLowerCase() === valueLower;
        });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            // Check for duplicate (case-insensitive)
            if (isDuplicate(values.key, values.value)) {
                message.error("A validation rule with this key and value already exists.");
                setLoading(false);
                return;
            }

            if (mode === 'create') {
                const createRequest: CreateValidationRequest = {
                    key: values.key,
                    value: values.value
                };

                await analyticsValidationService.createValidation(createRequest);
            } else if (mode === 'edit' && validation) {
                const updateRequest: UpdateValidationRequest = {
                    id: validation.id,
                    key: values.key,
                    value: values.value
                };

                await analyticsValidationService.updateValidation(updateRequest);
            }

            message.success(`Validation rule ${mode === 'create' ? 'created' : 'updated'} successfully`);
            onSuccess();
            onCancel();
        } catch (error) {
            console.error('Error submitting validation:', error);
            if (error instanceof Error) {
                message.error(error.message);
            } 
            // else {
            //     message.error('An error occurred while saving the validation rule');
            // }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={`${mode === 'create' ? 'Add' : 'Edit'} Validation Rule`}
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                >
                    {mode === 'create' ? 'Create' : 'Update'}
                </Button>
            ]}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="key"
                    label="Key"
                    rules={[
                        { required: true, message: 'Please select a key' }
                    ]}
                >
                    <Select placeholder="Select validation key">
                        <Select.Option value="Platform">Platform</Select.Option>
                        <Select.Option value="Channel">Channel</Select.Option>
                        <Select.Option value="Territory">Territory</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="value"
                    label="Value"
                    rules={[
                        { required: true, message: 'Please enter a value' },
                        { max: 1000, message: 'Value must be less than 1000 characters' }
                    ]}
                >
                    <Input.TextArea
                        placeholder="Enter validation value"
                        rows={3}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ValidationModal;
